import { fileModule } from "@/modules/file";
import { DropItem } from "react-aria-components";

import { toastModule } from "@/modules/toast";
import { TrackedEvent } from "@/domains/metrics";
import { trackEvent } from "@/domains/metrics";
import { MdsIconKind } from "@/design-system/components/icon/types";
import { MdsIcon } from "@/design-system/components/icon/MdsIcon";
import styled from "@emotion/styled";
import { importFilesAsSession } from "@/modules/file/import";
import { type AppStore } from "@/store";
import { EventContext } from "@/domains/metrics/context";

const MAX_FILES = 1_000;
const MAX_FILE_SIZE = 25 * 1024 * 1024; // set to match the limit on dropped files in Common Editor

export const importDroppedItems = async (
  items: DropItem[],
  store: AppStore,
  context: EventContext
) => {
  // Recursively get all file items from e.items
  const allFileItems = await fileModule.collectFileDropItems({ items });

  // Get file objects and their sizes
  const fileItemsWithSizes = await Promise.all(
    allFileItems.map(async fileItem => {
      const file = await fileItem.getFile();
      return { fileItem, size: file.size, type: fileItem.type, name: fileItem.name };
    })
  );

  const bigFilesCount = fileItemsWithSizes.filter(({ size }) => size > MAX_FILE_SIZE).length;

  const importableFileItems = fileItemsWithSizes
    // Filter for just .md files
    .filter(
      ({ name, type }) =>
        name.toLowerCase().endsWith(".md") ||
        name.toLowerCase().endsWith(".txt") ||
        type === "text/markdown" ||
        type === "text/plain"
    )
    // filter out files that are too big
    .filter(({ size }) => size <= MAX_FILE_SIZE)
    .map(({ fileItem }) => fileItem);

  const tooManyFiles = importableFileItems.length > MAX_FILES;

  trackEvent(TrackedEvent.ImportFilesDropped, {
    num_files: allFileItems.length,
    num_filtered_in_files: importableFileItems.length,
    too_many_files: tooManyFiles,
    max_files: MAX_FILES,
    had_big_files: bigFilesCount > 0,
    num_big_files: bigFilesCount,
    context,
  });

  if (tooManyFiles) {
    toastModule.triggerToast({
      content: <MaxFilesToastContent />,
    });
    return;
  }

  if (bigFilesCount > 0) {
    toastModule.triggerToast({
      content: <BigFileToastContent />,
    });
  }

  if (importableFileItems.length > 0) {
    try {
      importFilesAsSession(importableFileItems, store);
    } catch (e) {
      console.error(e);
      toastModule.triggerToast({ content: "An error occurred while importing files" });
    }
  } else {
    toastModule.triggerToast({
      content: "Only Markdown and text files are supported",
    });
  }
};

const MaxFilesToastContent = () => {
  return (
    <StyledMaxFilesToastWrapper>
      <MdsIcon kind={MdsIconKind.Warning} />
      Imports are limited to {MAX_FILES} files at a time
    </StyledMaxFilesToastWrapper>
  );
};

const BigFileToastContent = () => {
  return (
    <StyledMaxFilesToastWrapper>
      <MdsIcon kind={MdsIconKind.Warning} />
      Files larger than 25MB are left out of imports
    </StyledMaxFilesToastWrapper>
  );
};
const StyledMaxFilesToastWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xsPlus};
`;
